import { Box, CircularProgress, Tab, Tabs, Typography } from "@mui/material"
import { Link, Outlet, useMatches, useNavigate, useParams, useSearch } from "@tanstack/react-router"
import { useMemo } from "react"
import type { FC } from "react"

import { CompanyHeaderCard } from "./CompanyHeaderCard"

import { BackButton } from "@/components/common/BackButton"
import { ErrorMessage } from "@/components/ErrorMessage/ErrorMessage"
import { CUSTOMER_ORDER_DELIVERY_DATE_REFERENCE_TO_LABEL, ORDER_TABS } from "@/screens/CustomerOrders/constants"
import { useGetCustomerCompanyDetails } from "@/screens/CustomerOrders/hooks/useGetCustomerCompanyDetails"
import colors from "@/styles/colors"

export const CustomerOrdersWorkspaceLayout: FC = () => {
  const { companyId, shipToLocationId } = useParams({ from: "/app/orders/$companyId/$shipToLocationId/_layout" })
  const matches = useMatches()
  const navigate = useNavigate()

  const combinationId = useSearch({
    strict: false,
    select: (search: { combinationId?: string }) => search.combinationId,
  })

  const { company, shipToLocation, preferredDeliveryDateReference, isFetching, error } = useGetCustomerCompanyDetails(
    companyId,
    shipToLocationId
  )

  const datePreference = preferredDeliveryDateReference
    ? CUSTOMER_ORDER_DELIVERY_DATE_REFERENCE_TO_LABEL[preferredDeliveryDateReference]
    : "-"

  const currentTab = useMemo(() => {
    const lastMatch = matches[matches.length - 1]
    const tabValue = ORDER_TABS.find((tab) => lastMatch.pathname.includes(tab.value))?.value
    return tabValue ?? ORDER_TABS[0].value
  }, [matches])

  const handleBackClick = () => {
    navigate({ to: "/app/orders" })
  }

  return (
    <Box className='flex flex-col gap-4 p-6'>
      <Box className='flex items-center gap-4'>
        <BackButton
          aria-label='back to customer orders'
          onClick={handleBackClick}
          data-testid='customer-orders.workspace-layout.back-button'
        />
        <Typography variant='h5' className='font-normal text-primary'>
          Customer Orders
        </Typography>
      </Box>

      {isFetching && (
        <Box className='flex items-center justify-center p-8'>
          <CircularProgress />
        </Box>
      )}

      {!isFetching && error && <ErrorMessage error={error} title='Failed to load workspace' />}

      {!isFetching && company && shipToLocation && (
        <CompanyHeaderCard company={company} shipToLocation={shipToLocation} datePreference={datePreference} />
      )}

      <Box>
        <Tabs value={currentTab}>
          {ORDER_TABS.map((tab) => (
            <Tab
              key={tab.value}
              label={tab.label}
              value={tab.value}
              component={Link}
              to={`./${tab.value}`}
              search={combinationId ? { combinationId } : undefined}
              sx={
                currentTab === tab.value
                  ? {
                      color: colors.primary.main,
                      fontWeight: 500,
                      fontSize: 14,
                      boxShadow: `inset 0 -2px 0 ${colors.primary.main}`,
                      px: 3,
                      py: 1,
                    }
                  : {
                      color: colors.grey[700],
                      fontWeight: 500,
                      fontSize: 14,
                    }
              }
            />
          ))}
        </Tabs>
      </Box>

      <Box className='flex min-h-0 grow flex-col'>
        <Outlet />
      </Box>
    </Box>
  )
}
